import { CONDUIT_SUBDIV } from "../sim/networks";
import { effectiveCells, moduleOperational, shipCellSize } from "../sim/ship";
import type { ModuleCategory, Ship, ShipModule } from "../sim/types";
import type { Renderer } from "./renderer";

const FLAG_DAMAGED       = 2;
const FLAG_HAS_INDICATOR = 8;
const FLAG_IS_POWERED    = 16;
const FLAG_GHOST         = 256;
const FLAG_ACTIVE        = 512;
const FLAG_NO_ARC        = 1024;

const DOCK_FLASH_HZ = 3;

/** Stable [0,1) seed per block grid cell — used to phase-offset the
 *  destruction arc hashes so individual blocks blink out of sync. */
function blockPhaseSeed(gx: number, gy: number): number {
  const h = ((gx | 0) * 73856093) ^ ((gy | 0) * 19349663);
  return (h >>> 0) / 4294967296;
}

const INDICATOR_CATEGORIES = new Set<ModuleCategory>([
  "reactor", "battery", "cockpit", "engine", "thruster",
  "weapon", "radar", "life_support", "cooling", "cargo",
  "docking",
]);

export function pushShipTiles(
  r: Renderer,
  ship: Ship,
  time: number,
  activeModule: ShipModule | null = null,
): void {
  const c = Math.cos(ship.body.rot);
  const s = Math.sin(ship.body.rot);
  const cell = shipCellSize(ship);
  const bvx = ship.body.vel.x;
  const bvy = ship.body.vel.y;
  const bAng = ship.body.angVel;
  const bpx = ship.body.pos.x;
  const bpy = ship.body.pos.y;

  for (const m of ship.modules) {
    const def = m.def;
    // Truss modules render in their own pass (below sparks/bullets).
    if (def.noCollision) continue;
    const uv = r.atlas.lookup.get(def.sprite);
    if (!uv) continue;

    const destroyed = m.hp <= 0;
    const ghost = !!m.illegal;
    const active = !ghost && m === activeModule;
    const indicator = def.hasStatusIndicator ?? INDICATOR_CATEGORIES.has(def.category);
    let flags = 0;
    if (destroyed) flags |= FLAG_DAMAGED;
    if (ghost) flags |= FLAG_GHOST;
    else if (active) flags |= FLAG_ACTIVE;
    if (ship.isAsteroid) flags |= FLAG_NO_ARC;
    if (!destroyed && !ghost && !active && !ship.isAsteroid && indicator) {
      flags |= FLAG_HAS_INDICATOR;
      if (moduleOperational(ship, m)) flags |= FLAG_IS_POWERED;
    }

    const eff = effectiveCells(def, m.facing);
    const wWorld = def.cells.w * cell;
    const hWorld = def.cells.h * cell;
    const lx = (m.gx + eff.w / 2) * cell - ship.com.x;
    const ly = (m.gy + eff.h / 2) * cell - ship.com.y;

    const maxHp = def.stats.hp;
    const damageFrac = maxHp > 0
      ? Math.max(0, Math.min(1, 1 - m.hp / maxHp))
      : 0;

    const cx = ship.body.pos.x + lx * c - ly * s;
    const cy = ship.body.pos.y + lx * s + ly * c;
    const rot = ship.body.rot + m.facing * (Math.PI / 2);

    const dx = cx - bpx;
    const dy = cy - bpy;
    const velX = bvx - bAng * dy;
    const velY = bvy + bAng * dx;

    r.pushTileRaw(
      cx, cy,
      wWorld, hWorld,
      rot, flags,
      uv.u, uv.v, uv.w, uv.h,
      destroyed ? 0 : m.tempK,
      damageFrac,
      def.stats.thermalEmissivity,
      velX, velY, bAng,
      blockPhaseSeed(m.gx, m.gy),
    );

    if (!destroyed && !ship.isAsteroid && def.category === "docking" && m.docking
        && moduleOperational(ship, m)) {
      const locked = m.docking.peerShipId != null;
      const showLight = locked || Math.sin(time * Math.PI * 2 * DOCK_FLASH_HZ) > 0;
      if (showLight) {
        const overlay = m.docking.mode === "board" ? "dock_light_board" : "dock_light_trade";
        const oUv = r.atlas.lookup.get(overlay);
        if (oUv) {
          r.pushTileRaw(
            cx, cy,
            wWorld, hWorld,
            rot, 0,
            oUv.u, oUv.v, oUv.w, oUv.h,
            0, 0, 1.0,
            velX, velY, bAng,
          );
        }
      }
    }
  }

  // Heat conduits MUST draw AFTER universal so the arrow sits on top of
  // the pipe regardless of insertion order.
  if (ship.isAsteroid) return;
  const subSize = cell / CONDUIT_SUBDIV;
  for (const cd of ship.conduits) {
    if (cd.type !== "universal") continue;
    drawConduit(r, ship, cd.sgx, cd.sgy, 0, "conduit_universal", subSize, c, s, bvx, bvy, bAng, bpx, bpy);
  }
  for (const cd of ship.conduits) {
    if (cd.type !== "heat") continue;
    drawConduit(r, ship, cd.sgx, cd.sgy, 0, "conduit_heat", subSize, c, s, bvx, bvy, bAng, bpx, bpy);
    drawConduit(r, ship, cd.sgx, cd.sgy, cd.facing, "conduit_heat_arrow", subSize, c, s, bvx, bvy, bAng, bpx, bpy);
  }
}

/** Truss modules: rendered in a separate pre-pass so they layer above
 *  stars but below sparks/bullets. */
export function pushShipTrussTiles(
  r: Renderer,
  ship: Ship,
  activeModule: ShipModule | null = null,
): void {
  const c = Math.cos(ship.body.rot);
  const s = Math.sin(ship.body.rot);
  const cell = shipCellSize(ship);
  const bvx = ship.body.vel.x;
  const bvy = ship.body.vel.y;
  const bAng = ship.body.angVel;
  const bpx = ship.body.pos.x;
  const bpy = ship.body.pos.y;

  for (const m of ship.modules) {
    const def = m.def;
    if (!def.noCollision) continue;
    const uv = r.atlas.lookup.get(def.sprite);
    if (!uv) continue;

    const destroyed = m.hp <= 0;
    const ghost = !!m.illegal;
    const active = !ghost && m === activeModule;
    let flags = 0;
    if (destroyed) flags |= FLAG_DAMAGED;
    if (ghost) flags |= FLAG_GHOST;
    else if (active) flags |= FLAG_ACTIVE;
    if (ship.isAsteroid) flags |= FLAG_NO_ARC;

    const eff = effectiveCells(def, m.facing);
    const wWorld = def.cells.w * cell;
    const hWorld = def.cells.h * cell;
    const lx = (m.gx + eff.w / 2) * cell - ship.com.x;
    const ly = (m.gy + eff.h / 2) * cell - ship.com.y;

    const maxHp = def.stats.hp;
    const damageFrac = maxHp > 0
      ? Math.max(0, Math.min(1, 1 - m.hp / maxHp))
      : 0;

    const cx = ship.body.pos.x + lx * c - ly * s;
    const cy = ship.body.pos.y + lx * s + ly * c;
    const rot = ship.body.rot + m.facing * (Math.PI / 2);

    const dx = cx - bpx;
    const dy = cy - bpy;
    const velX = bvx - bAng * dy;
    const velY = bvy + bAng * dx;

    r.pushTileRaw(
      cx, cy,
      wWorld, hWorld,
      rot, flags,
      uv.u, uv.v, uv.w, uv.h,
      destroyed ? 0 : m.tempK,
      damageFrac,
      def.stats.thermalEmissivity,
      velX, velY, bAng,
      blockPhaseSeed(m.gx, m.gy),
    );
  }
}

function drawConduit(
  r: Renderer, ship: Ship,
  sgx: number, sgy: number,
  facing: number, key: string,
  subSize: number,
  c: number, s: number,
  bvx: number, bvy: number, bAng: number, bpx: number, bpy: number,
): void {
  const uv = r.atlas.lookup.get(key);
  if (!uv) return;
  const lx = (sgx + 0.5) * subSize - ship.com.x;
  const ly = (sgy + 0.5) * subSize - ship.com.y;
  const wx = ship.body.pos.x + lx * c - ly * s;
  const wy = ship.body.pos.y + lx * s + ly * c;
  const rot = ship.body.rot + facing * (Math.PI / 2);
  const dx = wx - bpx;
  const dy = wy - bpy;
  r.pushTileRaw(
    wx, wy,
    subSize, subSize,
    rot, 0,
    uv.u, uv.v, uv.w, uv.h,
    0, 0, 1.0,
    bvx - bAng * dy, bvy + bAng * dx, bAng,
  );
}
